import {
  hasSecureTwinRoute,
  isPublicBrowseRoute,
  mayStaySignedInOnOpenRoute,
  ROUTES,
} from '@/constants/routes';
import { toHomeTabPath } from '@/util/homeTabPath';
import { withRouteQuery } from '@/util/routeQuery';

/**
 * The directory tab, named inside the secure group.
 *
 * Spelled out here rather than taken from the route constants because those carry an `Href`
 * type that a template literal cannot extend, and every destination this module returns is a
 * plain string by design — see the return contract below.
 */
const DIRECTORY_TAB_PATHNAME = '/(secure)/(tabs)/directory';

const SECURE_DIRECTORY_PATHNAME = `/(secure)${ROUTES.DIRECTORY}`;

export type OpenGroupReaderState = {
  pathname: string;
  /** The reader's search params, already serialized. Empty when there are none. */
  query: string;
  isAuthResolved: boolean;
  userId: string | null;
  isUserLoading: boolean;
  needsNpiVerification: boolean;
  redirectPath: string | null;
};

/**
 * Where a reader in the open group belongs, or `null` to leave them where they are.
 *
 * The open group is the app's public face, so the question it answers is narrow: a reader
 * with no account belongs here and is never moved, and a signed-in reader is moved into the
 * secure app unless the route they are on is one of the exceptions. The exceptions are a
 * list rather than control flow now, in the route constants, where the matching rules the
 * rest of the app uses already live.
 *
 * **Pure, and returning a string.** Both are deliberate. Pure, because this replaced a
 * fourteen-branch focus effect that could only be exercised by rendering a navigator, and a
 * rule nobody can test one case at a time is a rule that grows a case nobody noticed — which
 * is exactly how the sign-out route came to be missing from it. A string, because the layout
 * hands the result to a component whose effect compares its dependency by value; an object
 * href is a fresh reference every render and rebuilds the render loop that component exists
 * to prevent.
 *
 * **Order is load-bearing.** Every clause below is reachable only because the ones above it
 * did not match, and two pairs in particular have to stay in their current order: the stay
 * list sits above the verification detour, so a reader awaiting verification is still left on
 * a share link or an expert link; and the directory clause sits above it too, so the tab
 * route is where `/directory` goes for everyone rather than only for verified readers.
 *
 * **Unknown is not an answer.** The three gates at the top return `null` — stay — while the
 * session is still being restored or the user record is still in flight. `needsNpiVerification`
 * reads false for a reader whose record has not arrived, so deciding before then sends a
 * verified reader down the unverified path; the guards in the secure layout make the mirror
 * image of this decision for the same reason.
 */
export function resolveOpenGroupDestination(reader: OpenGroupReaderState): string | null {
  const { pathname, query, isAuthResolved, userId, isUserLoading, needsNpiVerification } = reader;

  const redirectPath = reader.redirectPath;

  if (!isAuthResolved || !userId || isUserLoading) {
    return null;
  }

  if (mayStaySignedInOnOpenRoute(pathname)) {
    return null;
  }

  if (pathname === ROUTES.DIRECTORY) {
    return withRouteQuery(DIRECTORY_TAB_PATHNAME, query);
  }

  if (needsNpiVerification) {
    if (isPublicBrowseRoute(pathname)) {
      return null;
    }

    return redirectPath ? toHomeTabPath(redirectPath) : SECURE_DIRECTORY_PATHNAME;
  }

  /*
   * Safe only because both survey routes take a survey *group* id and resolve the published
   * version themselves. An earlier version of this forward was reverted when the two
   * disagreed and it handed a definition id to a screen expecting a group id. If they ever
   * diverge again, this has to go with them.
   */
  if (pathname.startsWith(ROUTES.SURVEY_PATH_PREFIX)) {
    return withRouteQuery(`/(secure)${pathname}`, query);
  }

  if (redirectPath) {
    return toHomeTabPath(redirectPath);
  }

  if (hasSecureTwinRoute(pathname)) {
    const tabPath = toHomeTabPath(pathname);

    return withRouteQuery(tabPath === pathname ? `/(secure)${pathname}` : tabPath, query);
  }

  return withRouteQuery(SECURE_DIRECTORY_PATHNAME, query);
}
